"use client";
import { CategoryNode } from "@/types/pim";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useInventoryFilters } from "./InventoryFiltersContext";
import { useInventoryNav } from "./InventoryNavContext";

interface DesktopToolbarProps {
  selectedCategory: CategoryNode | null;
}

const Subcategories: React.FC<DesktopToolbarProps> = ({
  selectedCategory,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { navigate, isNavigating } = useInventoryNav();
  // Recalculated child counts for the current filters come from the server (null =
  // no active filters → fall back to the static itemCount on each child).
  const { subcategoryCounts } = useInventoryFilters();

  const getCount = (subcat: CategoryNode): number =>
    subcategoryCounts ? (subcategoryCounts[subcat.Id] ?? 0) : (subcat.itemCount ?? 0);

  const getSubcategoryLink = (subcat: CategoryNode) => {
    const base = pathname.startsWith('/inventory/all')
      // On the All view the chips are root categories — strip the "/all" segment so
      // they link to the normal category route (e.g. /inventory/forklifts).
      ? `${pathname.replace('/all', '')}/${subcat.Slug}`
      : `${pathname}/${subcat.Slug}`;

    // Carry the Location filter across category/subcategory navigation. Location is a
    // warehouse-level filter that applies to every category, so it should persist;
    // other (category-specific) attribute filters are intentionally dropped.
    const locationValues = searchParams.getAll('Location');
    if (locationValues.length === 0) {
      return base;
    }

    const params = new URLSearchParams();
    locationValues.forEach((value) => params.append('Location', value));
    return `${base}?${params.toString()}`;
  };

  return (
    <div className="bg-white border-gray-200 text-black">
      <div className="flex flex-col">
        {/* Dim + spinner while a navigation recalculates the chips/counts. */}
        <div
          className={`flex items-center gap-2 flex-wrap flex-1 p-3 transition-opacity ${isNavigating ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
          aria-busy={isNavigating}
        >
          {isNavigating && (
            <span
              className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-gcopper"
              aria-hidden="true"
            />
          )}
          {selectedCategory && selectedCategory?.children?.length > 0 &&
            [...selectedCategory.children]
              .filter(subcat => getCount(subcat) > 0)
              .sort((a, b) => a.Name.localeCompare(b.Name))
              .map((subcat) => {
                const href = getSubcategoryLink(subcat);
                return (
                  <Link
                    key={subcat.Id}
                    href={href}
                    scroll={false}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
                        return;
                      }
                      e.preventDefault();
                      navigate(href);
                    }}
                    className="shrink-0 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-100 active:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    <span>{subcat.Name} ({getCount(subcat)})</span>
                  </Link>
                );
              })}
        </div>
      </div>
    </div>
  );
};

export default Subcategories;